import { useRelativeTime } from "@/hooks";
import { useDatabaseStore, type Dataflow } from "@/stores/database";
import { logger } from "@/utils/debug";
import {
  ActionIcon,
  Button,
  Card,
  Flex,
  Menu,
  Modal,
  Text,
} from "@mantine/core";
import { IconBook2, IconDotsVertical, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { Link } from "react-router";
import "./WorkspaceCard.css";

export function WorkspaceCard({ dataflow }: { dataflow: Dataflow }) {
  const deleteDataflow = useDatabaseStore((state) => state.deleteDataflow);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const lastUpdated = dataflow.datetime_updated || dataflow.datetime_created;
  const timeAgo = useRelativeTime({
    date: lastUpdated,
  });

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const { error } = await deleteDataflow(dataflow.dataflow_id);
      if (error) {
        logger.error("Failed to delete dataflow:", error);
      } else {
        setShowDeleteModal(false);
      }
    } catch (err) {
      logger.error("Error deleting dataflow:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Card
        radius="sm"
        p="md"
        withBorder
        w={300}
        h={130}
        style={{
          borderLeft: `8px solid var(--mantine-color-eu_flag-filled)`,
        }}
      >
        <Flex direction="column" h="100%">
          <Flex flex={1} justify="space-between" direction="row">
            <Flex direction="column" gap={4}>
              <IconBook2
                size={20}
                color="var(--mantine-color-eu_flag-filled)"
              />
              <Link
                to={`/build/${dataflow.project_id}/${dataflow.dataflow_id}`}
                style={{ textDecoration: "none", display: "inline-block" }}
              >
                <Text fz={16} fw={500} className="workspace-card-title">
                  {dataflow.dataflow_name}
                </Text>
              </Link>
            </Flex>
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="md"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconDotsVertical size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconTrash size={14} />}
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteModal(true);
                  }}
                >
                  Delete Workspace
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
          <Flex>
            <Text fz={14} c="gray.9">
              {timeAgo}
            </Text>
          </Flex>
        </Flex>
      </Card>

      <Modal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Workspace"
        centered
        size="sm"
      >
        <Text mb="md">
          Are you sure you want to delete "{dataflow.dataflow_name}"? This
          action cannot be undone.
        </Text>
        <Flex justify="flex-end" gap="sm" pt="lg">
          <Button
            variant="outline"
            c="gray.9"
            fullWidth
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            color="red"
            onClick={handleDelete}
            loading={isDeleting}
          >
            Delete
          </Button>
        </Flex>
      </Modal>
    </>
  );
}
